import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ActivityAsyncAdapter } from 'app/model/adapters/activities/activity.adapter';

import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { DataflowRefreshActivity } from 'app/model/pipeline/dataflow-refresh.activity';
import { ReferenceAdapter } from '../reference.adapter';
import { AdapterContext, Credential, FabricDataflowLinkedService, LinkedService, ModelConstants, convertParametersToModel } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import { IRefreshDataflowActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { toParameterClass, toParameterObject } from '../linkedservices/parameter-config.util';
import { convertParameterValue } from 'app/shared/utils/parameter-converter.util';
import { CopilotNameMapperService } from 'app/services/copilot-name-mapper.service';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';
import { getFabricLsForParameterizedConnections } from '../utils';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { isMdfDataflowActivity } from 'app/model/adapters/dataflow/dataflow-workload-payload.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(DataflowRefreshActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        type: '*',
        description: '*',
        linkedServiceName: async (context: AdapterContext) => {
            const exposureControlService = await getServiceInstance<ExposureControlService>(
                context.contextData,
                'ExposureControlService'
            );
            return GlobalVariables.useDataflowConnection && exposureControlService.UseTokenServiceInDataFlowActivity ? linkedServiceReferenceWithoutParameterizationRemainer : undefined;
        },
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            dataflowId: '*',
            workspaceId: '*',
            notifyOption: '*',
            dataflowType: '*',
            parameters: '*',
            compute: '*',
            traceLevel: '*',
            runConcurrently: '*',
            continueOnError: '*',
            staging: {
                linkedService: '*',
                linkedServiceName: '*',
                folderPath: '*',
                externalReferences: '*'
            },
            continuationSettings: {
                customizedCheckpointKey: '*'
            },
            sparkTransformName: '*',
            executeMashupQueriesOnly: '*'
        },
        externalReferences: '*'
    }
})
export class DataflowRefreshActivityAdapter extends ActivityAsyncAdapter<DataflowRefreshActivity, Model.DataflowRefreshActivity, IRefreshDataflowActivityContext> {

    public model(): IModel {
        return Metamodel.modelFor(DataflowRefreshActivity);
    }

    public override async toFabricCopilotContext(activity: DataflowRefreshActivity): Promise<IRefreshDataflowActivityContext> {
        if (activity) {
            const activityContext: IRefreshDataflowActivityContext = {
                ...(await super.toFabricCopilotContext(activity)),
                DataflowId: ValueAdapter.toResource(activity, DataflowRefreshActivity.DATAFLOWID, true),
                WorkspaceId: ValueAdapter.toResource(activity, DataflowRefreshActivity.WORKSPACEID, true)
            };
            const nameMapperService = await getServiceInstance<CopilotNameMapperService>(
                this.context.contextData, 
                'CopilotNameMapperService'
            );

            const [refreshDataflowName, refreshDataflowWorkspaceName] = await Promise.all([
                nameMapperService.getTridentArtifactNameById('pbidataflow', activityContext.DataflowId),
                nameMapperService.getTridentArtifactNameById('workspace', activityContext.WorkspaceId)
            ]);

            activityContext.DataflowName = refreshDataflowName;
            activityContext.WorkspaceName = refreshDataflowWorkspaceName;
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IRefreshDataflowActivityContext,
        model?: DataflowRefreshActivity
    ): Promise<DataflowRefreshActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            ValueAdapter.toModel(model, DataflowRefreshActivity.DATAFLOWID, context.DataflowId);
            ValueAdapter.toModel(model, DataflowRefreshActivity.WORKSPACEID, context.WorkspaceId);
        }
        return model;
    }

    public async postToModel(activity: DataflowRefreshActivity): Promise<void> {
        await super.postToModel(activity);
        if (!__IS_FABRIC_DPE__) {
            return;
        }
        const nameMapperService = await getServiceInstance<CopilotNameMapperService>(
            this.context.contextData,
            'CopilotNameMapperService'
        );
        const dataflowId = ValueAdapter.toResource(activity, DataflowRefreshActivity.DATAFLOWID, true);
        const workspaceId = ValueAdapter.toResource(activity, DataflowRefreshActivity.WORKSPACEID, true);

        if (dataflowId && !isLogicAppExpression(dataflowId)) {
            nameMapperService.getTridentArtifactNameById('pbidataflow', dataflowId);
        }
        if (workspaceId && !isLogicAppExpression(workspaceId)) {
            nameMapperService.getTridentArtifactNameById('workspace', workspaceId);
        }
    }

    protected async _toResource(activity: DataflowRefreshActivity): Promise<Model.DataflowRefreshActivity> {
        const modelTridentDataFlowActivity: Model.DataflowRefreshActivity = super._getCommonActivityProperties(activity, 'RefreshDataflow') as Model.DataflowRefreshActivity;

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelTridentDataFlowActivity, parsed);
        }

        const exposureControlService = await getServiceInstance<ExposureControlService>(
            this.context.contextData,
            'ExposureControlService'
        );

        if (__IS_FABRIC_DPE__) {
            modelTridentDataFlowActivity.typeProperties = {
                dataflowId: activity.dataflow?.dataflowId ?? ValueAdapter.toResource(activity, DataflowRefreshActivity.DATAFLOWID),
                workspaceId: activity.dataflow?.workspaceId ?? ValueAdapter.toResource(activity, DataflowRefreshActivity.WORKSPACEID),
                notifyOption: activity.notificationValue
            };

            modelTridentDataFlowActivity.typeProperties.dataflowType = 
                activity.dataflow?.version ?? ValueAdapter.toResource(activity, DataflowRefreshActivity.DATAFLOWTYPE);

            if (GlobalVariables.useDataflowConnection && exposureControlService.UseTokenServiceInDataFlowActivity && activity.linkedService) {
                if (activity.linkedService) {
                    modelTridentDataFlowActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.linkedService);
                }
            }

            // "MDF mode" is either a legacy single-MDF artifact (dataflowType discriminator)
            // or a native Dataflow Gen2 artifact where the user picked a specific
            // MDF query (sparkTransformName set, executeMashupQueriesOnly false). Centralized in
            // `isMdfDataflowActivity`; once legacy is removed, the helper's legacy branch goes away.
            const isNativeMdfPicked = !activity.executeMashupQueriesOnly && !stringIsNullOrEmpty(activity.sparkTransformName);
            const isMdfMode = isMdfDataflowActivity({
                dataflowType: modelTridentDataFlowActivity.typeProperties.dataflowType,
                sparkTransformName: activity.sparkTransformName,
                executeMashupQueriesOnly: activity.executeMashupQueriesOnly
            });

            if (isMdfMode) {
                modelTridentDataFlowActivity.typeProperties.parameters = toParameterObject(activity.parameters, false);

                // Compute settings (no IR for Fabric MDF)
                modelTridentDataFlowActivity.typeProperties.compute = {
                    coreCount: ValueAdapter.toResource(activity, DataflowRefreshActivity.CORE_COUNT),
                    computeType: ValueAdapter.toResource(activity, DataflowRefreshActivity.COMPUTE_TYPE)
                };

                // Logging
                modelTridentDataFlowActivity.typeProperties.traceLevel = ValueAdapter.toResource(activity, DataflowRefreshActivity.TRACE_LEVEL);

                // Sink properties
                modelTridentDataFlowActivity.typeProperties.runConcurrently = ValueAdapter.toResource(activity, DataflowRefreshActivity.RUN_CONCURRENTLY);
                modelTridentDataFlowActivity.typeProperties.continueOnError = ValueAdapter.toResource(activity, DataflowRefreshActivity.CONTINUE_ON_ERROR);

                // Staging — only serialize when there's actual staging data
                const stagingFolderPath = ValueAdapter.toResource(activity, DataflowRefreshActivity.STAGING_STORAGE_PATH);
                if (activity.stagingLinkedService || stagingFolderPath) {
                    modelTridentDataFlowActivity.typeProperties.staging = {
                        linkedService: activity.stagingLinkedService
                            ? this._toLinkedServiceReferenceModel(activity.stagingLinkedService, activity.stagingLinkedServiceParameters) : undefined,
                        folderPath: stagingFolderPath
                    };
                }

                // Checkpoint key (CDC)
                const customizedKey = ValueAdapter.toResource(activity, DataflowRefreshActivity.CUSTOMIZED_CHECKPOINT_KEY);
                if (!stringIsNullOrEmpty(customizedKey)) {
                    modelTridentDataFlowActivity.typeProperties.continuationSettings = {
                        customizedCheckpointKey: customizedKey
                    };
                }

                // Native Dataflow Gen2 selectors. When the user picked
                // a specific MDF query, emit both sparkTransformName and executeMashupQueriesOnly=false.
                // Legacy single-MDF artifacts leave both undefined.
                if (isNativeMdfPicked) {
                    modelTridentDataFlowActivity.typeProperties.sparkTransformName = activity.sparkTransformName;
                    modelTridentDataFlowActivity.typeProperties.executeMashupQueriesOnly = false;
                }
            } else {
                // Native "run all M queries" path: emit executeMashupQueriesOnly=true only,
                // sparkTransformName must not be present in the payload.
                if (activity.executeMashupQueriesOnly) {
                    modelTridentDataFlowActivity.typeProperties.executeMashupQueriesOnly = true;
                }
                modelTridentDataFlowActivity.typeProperties.parameters = toParameterObject(activity.parameters, true);
                if (modelTridentDataFlowActivity.typeProperties.parameters) {
                    for (const parameterName in modelTridentDataFlowActivity.typeProperties.parameters) {
                        const parameter = modelTridentDataFlowActivity.typeProperties.parameters[parameterName];
                            parameter.value = convertParameterValue(parameter.value, parameter.type, 'DataflowRefreshActivityAdapter',
                                this.ensureLogger(), true /* don't parse as an object */);
                        if (GlobalVariables.useDataflowNewParameterUi) {
                            parameter.type = this._convertFabricTypeToAdfType(parameter.type);
                        }
                    }
                }
            }
        }
        else {
            modelTridentDataFlowActivity.typeProperties = {
                dataflowId: ValueAdapter.toResource(activity, DataflowRefreshActivity.DATAFLOWID),
                workspaceId: ValueAdapter.toResource(activity, DataflowRefreshActivity.WORKSPACEID),
                notifyOption: ValueAdapter.toResource(activity, DataflowRefreshActivity.NOTIFICATIONVALUE),
                credential: activity.credential
                    ? ReferenceAdapter.toResource(activity.credential, this.context) as Model.CredentialReference
                    : undefined
            };

            modelTridentDataFlowActivity.typeProperties.dataflowType = 
                ValueAdapter.toResource(activity, DataflowRefreshActivity.DATAFLOWTYPE);

            if (GlobalVariables.useDataflowConnection && exposureControlService.UseTokenServiceInDataFlowActivity && activity.linkedService) {
                if (activity.linkedService) {
                    modelTridentDataFlowActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.linkedService);
                }
            }

            if (this.context.adapterConfig.convertToFabric) {
                if (!stringIsNullOrEmpty(activity.sparkTransformName)) {
                    modelTridentDataFlowActivity.typeProperties.sparkTransformName = activity.sparkTransformName;
                }
                if (activity.executeMashupQueriesOnly !== undefined && activity.executeMashupQueriesOnly !== null) {
                    modelTridentDataFlowActivity.typeProperties.executeMashupQueriesOnly = activity.executeMashupQueriesOnly;
                }

                // Emit IR / staging / checkpoint / parameters when migrating to Fabric MDF.
                modelTridentDataFlowActivity.typeProperties.compute = {
                    coreCount: ValueAdapter.toResource(activity, DataflowRefreshActivity.CORE_COUNT),
                    computeType: ValueAdapter.toResource(activity, DataflowRefreshActivity.COMPUTE_TYPE)
                };

                const traceLevel = ValueAdapter.toResource(activity, DataflowRefreshActivity.TRACE_LEVEL);
                if (traceLevel !== undefined && traceLevel !== null) {
                    modelTridentDataFlowActivity.typeProperties.traceLevel = traceLevel;
                }
                const runConcurrently = ValueAdapter.toResource(activity, DataflowRefreshActivity.RUN_CONCURRENTLY);
                if (runConcurrently !== undefined && runConcurrently !== null) {
                    modelTridentDataFlowActivity.typeProperties.runConcurrently = runConcurrently;
                }
                const continueOnError = ValueAdapter.toResource(activity, DataflowRefreshActivity.CONTINUE_ON_ERROR);
                if (continueOnError !== undefined && continueOnError !== null) {
                    modelTridentDataFlowActivity.typeProperties.continueOnError = continueOnError;
                }

                const stagingFolderPath = ValueAdapter.toResource(activity, DataflowRefreshActivity.STAGING_STORAGE_PATH);
                if (activity.stagingLinkedService || !stringIsNullOrEmpty(stagingFolderPath)) {
                    modelTridentDataFlowActivity.typeProperties.staging = {
                        linkedService: activity.stagingLinkedService
                            ? this._toLinkedServiceReferenceModel(activity.stagingLinkedService, activity.stagingLinkedServiceParameters)
                            : undefined,
                        folderPath: stagingFolderPath
                    };
                }

                const customizedKey = ValueAdapter.toResource(activity, DataflowRefreshActivity.CUSTOMIZED_CHECKPOINT_KEY);
                if (!stringIsNullOrEmpty(customizedKey)) {
                    modelTridentDataFlowActivity.typeProperties.continuationSettings = {
                        customizedCheckpointKey: customizedKey
                    };
                }

                modelTridentDataFlowActivity.typeProperties.parameters = toParameterObject(activity.parameters, false);
            }
        }

        return modelTridentDataFlowActivity;
    }

    protected async _toModel(modelActivity: Model.DataflowRefreshActivity): Promise<DataflowRefreshActivity> {
        const activity: DataflowRefreshActivity = DataflowRefreshActivity.MODEL.create<DataflowRefreshActivity>(modelActivity.name, modelActivity.description);
        this._updatedCommonProperties(modelActivity, activity);

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, DataflowRefreshActivity.MODEL));
        ValueAdapter.toModel(activity, DataflowRefreshActivity.DATAFLOWID, modelActivity.typeProperties.dataflowId);
        ValueAdapter.toModel(activity, DataflowRefreshActivity.WORKSPACEID, modelActivity.typeProperties.workspaceId);
        ValueAdapter.toModel(activity, DataflowRefreshActivity.NOTIFICATIONVALUE, modelActivity.typeProperties.notifyOption);
        
        const exposureControlService = await getServiceInstance<ExposureControlService>(
            this.context.contextData,
            'ExposureControlService'
        );

        ValueAdapter.toModel(activity, DataflowRefreshActivity.DATAFLOWTYPE, modelActivity.typeProperties.dataflowType);

        if (GlobalVariables.useDataflowConnection && exposureControlService.UseTokenServiceInDataFlowActivity) {
            if (modelActivity.linkedServiceName != undefined) {
                const ls = getFabricLsForParameterizedConnections(
                    modelActivity.linkedServiceName,
                    ModelConstants.fabricDataflowLinkedService,
                    activity
                );
                activity.linkedService = ls ? (ls as FabricDataflowLinkedService) : ReferenceAdapter.toProxyModel<FabricDataflowLinkedService>(Metamodel.modelFor(FabricDataflowLinkedService),
                    modelActivity.linkedServiceName,
                    this.context);
            }
        }

        // Mirror _toResource gating: legacy MDF discriminator OR native MDF selection.
        // Centralized in `isMdfDataflowActivity`; legacy branch goes away with legacy support.
        const isMdfModeModel = isMdfDataflowActivity(modelActivity.typeProperties);

        if (isMdfModeModel) {
            activity.parameters = toParameterClass(modelActivity.typeProperties.parameters, activity, false);

            // Deserialize compute settings
            if (modelActivity.typeProperties.compute) {
                ValueAdapter.toModel(activity, DataflowRefreshActivity.CORE_COUNT, modelActivity.typeProperties.compute.coreCount);
                ValueAdapter.toModel(activity, DataflowRefreshActivity.COMPUTE_TYPE, modelActivity.typeProperties.compute.computeType);
            }

            // Deserialize logging
            if (modelActivity.typeProperties.traceLevel !== undefined) {
                ValueAdapter.toModel(activity, DataflowRefreshActivity.TRACE_LEVEL, modelActivity.typeProperties.traceLevel);
            }

            // Deserialize sink properties
            if (modelActivity.typeProperties.runConcurrently !== undefined) {
                ValueAdapter.toModel(activity, DataflowRefreshActivity.RUN_CONCURRENTLY, modelActivity.typeProperties.runConcurrently);
            }
            if (modelActivity.typeProperties.continueOnError !== undefined) {
                ValueAdapter.toModel(activity, DataflowRefreshActivity.CONTINUE_ON_ERROR, modelActivity.typeProperties.continueOnError);
            }

            // Deserialize staging
            if (modelActivity.typeProperties.staging) {
                const staging = modelActivity.typeProperties.staging;
                const stagingLinkedServiceRef = staging.linkedService || staging.linkedServiceName;
                if (stagingLinkedServiceRef) {
                    activity.stagingLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(Metamodel.modelFor(LinkedService),
                        stagingLinkedServiceRef, this.context);
                    if (stagingLinkedServiceRef.parameters) {
                        convertParametersToModel(activity,
                            !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                            activity.stagingLinkedService,
                            activity.stagingLinkedServiceParameters,
                            stagingLinkedServiceRef.parameters);
                    }
                }
                ValueAdapter.toModel(activity, DataflowRefreshActivity.STAGING_STORAGE_PATH, staging.folderPath);
            }

            // Deserialize checkpoint key
            if (modelActivity.typeProperties.continuationSettings && modelActivity.typeProperties.continuationSettings.customizedCheckpointKey) {
                ValueAdapter.toModel(activity, DataflowRefreshActivity.CUSTOMIZED_CHECKPOINT_KEY, modelActivity.typeProperties.continuationSettings.customizedCheckpointKey);
            }

            // Native MDF picked: hydrate both selectors.
            if (modelActivity.typeProperties.executeMashupQueriesOnly === false
                && !stringIsNullOrEmpty(modelActivity.typeProperties.sparkTransformName)) {
                activity.sparkTransformName = modelActivity.typeProperties.sparkTransformName;
                activity.executeMashupQueriesOnly = false;
            }
        } else {
            // Native "run all M queries" path.
            if (modelActivity.typeProperties.executeMashupQueriesOnly === true) {
                activity.executeMashupQueriesOnly = true;
            }
            activity.parameters = toParameterClass(modelActivity.typeProperties.parameters, activity, true);
            if (GlobalVariables.useDataflowNewParameterUi) {
                activity.parameters.forEach((parameter) => {
                    parameter.type = this._convertAdfTypeToFabricType(parameter.type);
                });
            }
        }

        activity.credential = ReferenceAdapter.toProxyModel<Credential>(Metamodel.modelFor(Credential), modelActivity.typeProperties.credential, this.context);
        
        return activity;
    }

    // NOTE: anredd - We have to perform this inconvenient mapping because there is a mismatch between types in Pipeline UI, ADMS, and Discovery API
    // ADMS expects ADF types in the input payload but uses Power Query M types in job submission while Discovery API uses Fabric variable types 
    // Eventually we can consolidate after release 
    private _convertFabricTypeToAdfType(dataflowParamType: string): string {
        switch (dataflowParamType) {
            case 'Boolean':
                return 'Bool';
            case 'Number':
            case 'Double':
                return 'Float';
            case 'Integer':
                return 'Int';
            // Array, DateTime, DateTimeZone, Date, Time, Duration, String
            default:
                return dataflowParamType;
        }
    }

    private _convertAdfTypeToFabricType(dataflowType: string): string {
        switch (dataflowType) {
            case 'Bool':
                return 'Boolean';
            case 'Float':
                return 'Number';
            case 'Int':
                return 'Integer';
            case 'String':
            default:
                return dataflowType;
        }
    }
}
